import { forwardRef, useCallback } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { ButtonClose } from '@hivebrite/design-system/Button';
import { type Theme } from '@hivebrite/design-system/Theme';

import VideoPlayer from 'hb-react/shared/components/VideoPlayer';
import { type GlobalStorageFile } from 'hb-react/shared/hooks/useGlobalStorageV2/types';
import { isInLoadingState } from 'hb-react/shared/hooks/useGlobalStorageV2/utils';
import useVirusScanningEnabled from 'hb-react/shared/hooks/useVirusScanningEnabled';

import { useQuillEditorContext } from '../contexts/QuillEditorContext';
import MediaUploadError from '../MediaUploadError/index';

interface VideoProps {
  error?: string | null;
  onRemove?: (file: GlobalStorageFile) => void;
  video: GlobalStorageFile & { loading?: boolean, url?: string; };
}

const Video = ({ video, onRemove, error }: VideoProps) => {
  const virusScanningEnabled = useVirusScanningEnabled();

  // HACK: While we support QuilEditor V1 and V2/3, ignore the virus scan status
  // if attachment is not a GlobalStorageFile (i.e. has a loading property OR no key property)
  const keys = Object.keys(video);
  const isLoading =
    keys.includes('loading') || !keys.includes('key')
      ? video.loading
      : isInLoadingState(video, virusScanningEnabled);

  const handleRemove = useCallback(() => onRemove?.(video), [onRemove, video]);

  return (
    <WrapperVideo data-testid="quill-preview-video">
      <VideoPreviewWrapper $error={Boolean(error)}>
        <VideoPlayer file={video.file} isLoading={isLoading} />
        {!isLoading && error && <MediaUploadError error={error} />}
      </VideoPreviewWrapper>
      {!isLoading && (
        <ButtonCloseWrapper>
          <ButtonClose
            aria-label={__('Remove')}
            variant="inversed"
            onClick={handleRemove}
          />
        </ButtonCloseWrapper>
      )}
    </WrapperVideo>
  );
};

const Videos = forwardRef<HTMLDivElement, {}>((_props, ref) => {
  const { onRemoveFile, videos: allVideos } = useQuillEditorContext();

  const videos = allVideos?.filter((video) => !video.isRemoved);

  if (!videos?.length) {
    return null;
  }

  return (
    <WrapperVideos ref={ref}>
      {videos.map((video) => (
        <Video
          key={video.key || video.id}
          video={video}
          onRemove={onRemoveFile}
          error={video.error}
        />
      ))}
    </WrapperVideos>
  );
});

export default Videos;

const WrapperVideos = styled(Box).attrs(() => ({
  mb: 6,
  pt: 1,
  px: 4,
}))`
  > * + * {
    margin-top: ${(props) => props.theme.space[4]}px;
  }
`;
const WrapperVideo = styled(Box).attrs(() => ({
  minHeight: 150,
  minWidth: 150,
  maxWidth: 400,
  width: '100%',
}))`
  position: relative;
  border-radius: ${({ theme }) => theme.themeVariables.globalBorderRadius}px;
  overflow: hidden;
`;

const VideoPreviewWrapper = styled(Box).attrs(() => ({
  alignItems: 'center',
  bg: 'cloud',
  color: 'dove',
  justifyContent: 'center',
  width: '100%',
}))<{ $error: boolean }>`
  border-radius: ${({ theme }: { theme: Theme }) =>
    theme.themeVariables.globalBorderRadius}px;
  outline: 1px solid
    ${(props) => (props.$error ? props.theme.colors.fire : 'transparent')};
`;

const ButtonCloseWrapper = styled(Box)`
  position: absolute;
  right: 10px;
  top: 10px;
`;
